import { useState, useEffect } from 'react';

interface SplashScreenProps {
  onComplete: () => void;
}

const LOGO_URL = 'https://static.readdy.ai/image/8e0505aa0635a0a18473d6b2a0ce03ab/89283b399114b6ed153fad3b8fa74955.png';

const SERVICES = [
  'Professional Consulting',
  'Talent and Support Solutions',
  'Service Desk User Support',
  'ERP Managed Services',
  'Advisor and Strategy',
  'Future Ready AI Innovation',
];

export function SplashScreen({ onComplete }: SplashScreenProps) {
  const [logoVisible, setLogoVisible] = useState(false);
  const [logoGlow, setLogoGlow] = useState(false);
  const [lineVisible, setLineVisible] = useState(false);
  const [serviceIndex, setServiceIndex] = useState(-1);
  const [serviceVisible, setServiceVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Logo entrance
    timers.push(setTimeout(() => setLogoVisible(true), 200));
    timers.push(setTimeout(() => setLogoGlow(true), 700));
    timers.push(setTimeout(() => setLineVisible(true), 900));

    // Cycle through services
    const SERVICE_DURATION = 520;
    const FADE_DURATION = 220;

    SERVICES.forEach((_, i) => {
      const showAt = 1100 + i * SERVICE_DURATION;
      const hideAt = showAt + SERVICE_DURATION - FADE_DURATION;

      timers.push(setTimeout(() => {
        setServiceIndex(i);
        setServiceVisible(true);
      }, showAt));

      timers.push(setTimeout(() => {
        setServiceVisible(false);
      }, hideAt));
    });

    // Total service cycle ends at ~1100 + 6*520 = 4220ms
    // Hold last message a bit, then exit
    timers.push(setTimeout(() => {
      setServiceIndex(SERVICES.length - 1);
      setServiceVisible(true);
    }, 1100 + SERVICES.length * SERVICE_DURATION));

    timers.push(setTimeout(() => setExiting(true), 1100 + SERVICES.length * SERVICE_DURATION + 700));
    timers.push(setTimeout(() => onComplete(), 1100 + SERVICES.length * SERVICE_DURATION + 1400));

    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{
        background: '#0a0a0a',
        opacity: exiting ? 0 : 1,
        transition: exiting ? 'opacity 0.7s ease-in-out' : 'none',
        pointerEvents: exiting ? 'none' : 'all',
      }}
    >
      {/* Background radial glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute rounded-full"
          style={{
            width: '700px', height: '700px',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(220,38,38,0.07) 0%, transparent 65%)',
            opacity: logoGlow ? 1 : 0,
            transition: 'opacity 1.2s ease',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: '900px', height: '900px',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(220,38,38,0.04) 0%, transparent 60%)',
            opacity: logoGlow ? 1 : 0,
            transition: 'opacity 1.5s ease 0.3s',
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative flex flex-col items-center" style={{ gap: '36px' }}>

        {/* Logo — big, animated entrance + pulse glow */}
        <div
          style={{
            opacity: logoVisible ? 1 : 0,
            transform: logoVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.88)',
            transition: 'opacity 0.85s cubic-bezier(0.22,1,0.36,1), transform 0.85s cubic-bezier(0.22,1,0.36,1)',
            filter: logoGlow
              ? 'drop-shadow(0 0 32px rgba(220,38,38,0.45)) drop-shadow(0 0 64px rgba(220,38,38,0.2))'
              : 'drop-shadow(0 0 0px rgba(220,38,38,0))',
            animation: logoGlow ? 'logoPulse 3s ease-in-out infinite' : 'none',
          }}
        >
          <img
            src={LOGO_URL}
            alt="Cendien"
            style={{
              height: 'clamp(100px, 14vw, 160px)',
              width: 'auto',
              display: 'block',
            }}
          />
        </div>

        {/* Animated divider line */}
        <div
          style={{
            height: '1px',
            width: lineVisible ? '340px' : '0px',
            background: 'linear-gradient(to right, transparent, rgba(220,38,38,0.6), transparent)',
            opacity: lineVisible ? 1 : 0,
            transition: 'width 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s, opacity 0.5s ease 0.1s',
            marginTop: '-12px',
          }}
        />

        {/* Cycling service messages */}
        <div
          style={{
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '-8px',
            minWidth: '360px',
          }}
        >
          {serviceIndex >= 0 && (
            <span
              key={serviceIndex}
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '13px',
                fontWeight: 300,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                opacity: serviceVisible ? 1 : 0,
                transform: serviceVisible ? 'translateY(0px)' : 'translateY(8px)',
                transition: 'opacity 0.22s ease, transform 0.22s ease',
                display: 'block',
                textAlign: 'center',
              }}
            >
              {SERVICES[serviceIndex]}
            </span>
          )}
        </div>

        {/* Service dots indicator */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginTop: '-16px',
            opacity: lineVisible ? 1 : 0,
            transition: 'opacity 0.5s ease 0.4s',
          }}
        >
          {SERVICES.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === serviceIndex ? '20px' : '5px',
                height: '3px',
                borderRadius: '2px',
                background: i === serviceIndex ? 'rgba(220,38,38,0.9)' : 'rgba(255,255,255,0.18)',
                transition: 'width 0.3s ease, background 0.3s ease',
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom label */}
      <div
        className="absolute bottom-10 text-center"
        style={{
          opacity: lineVisible ? 1 : 0,
          transition: 'opacity 0.6s ease 0.8s',
        }}
      >
        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase' }}>
          Carrollton, TX &nbsp;·&nbsp; Empowering Business Since 1999
        </span>
      </div>

      {/* Keyframe for logo pulse */}
      <style>{`
        @keyframes logoPulse {
          0%, 100% { filter: drop-shadow(0 0 28px rgba(220,38,38,0.4)) drop-shadow(0 0 56px rgba(220,38,38,0.18)); }
          50%       { filter: drop-shadow(0 0 44px rgba(220,38,38,0.65)) drop-shadow(0 0 80px rgba(220,38,38,0.28)); }
        }
      `}</style>
    </div>
  );
}
